import React, { useState } from 'react';
import { ChevronDownIcon, Loader2Icon } from 'lucide-react';
import { Car } from '../types/car';
import { submitLead } from '../utils/leads';
import { CarSummary } from './CarSummary';
import { FormField, inputClass } from './FormField';
import { SubmitError } from './SubmitError';
import { SuccessState } from './SuccessState';
interface TradeInFormProps {
  car: Car;
  onClose: () => void;
}
type Fields = {
  brand: string;
  model: string;
  year: string;
  km: string;
  condition: string;
  name: string;
  phone: string;
  email: string;
};
const years = Array.from({
  length: 20
}, (_, i) => String(2026 - i));
const conditions = ['Excelente', 'Muy bueno', 'Bueno', 'Con detalles'];
export function TradeInForm({
  car,
  onClose
}: TradeInFormProps) {
  const [fields, setFields] = useState<Fields>({
    brand: '',
    model: '',
    year: '',
    km: '',
    condition: '',
    name: '',
    phone: '',
    email: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Fields, string>>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const update = (key: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFields(prev => ({
      ...prev,
      [key]: e.target.value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  };
  if (status === 'success') {
    return <SuccessState title="Recibimos tu permuta" message={`Vamos a cotizar tu ${fields.brand} ${fields.model} ${fields.year} y te enviamos la propuesta para el ${car.brand} ${car.model} en 24 h hábiles.`} onClose={onClose} data-id="element-368" data-mp="tsrvmh7upqc5" />;
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Partial<Record<keyof Fields, string>> = {};
    if (!fields.brand.trim()) next.brand = 'Ingresá la marca';
    if (!fields.model.trim()) next.model = 'Ingresá el modelo';
    if (!fields.year) next.year = 'Elegí el año';
    if (!fields.km.trim() || Number.isNaN(Number(fields.km))) next.km = 'Ingresá los kilómetros';
    if (!fields.condition) next.condition = 'Elegí el estado';
    if (!fields.name.trim()) next.name = 'Ingresá tu nombre';
    if (fields.phone.replace(/\D/g, '').length < 8) next.phone = 'Ingresá un teléfono válido';
    if (fields.email && !/^\S+@\S+\.\S+$/.test(fields.email)) next.email = 'Ingresá un email válido';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('loading');
    try {
      await submitLead({
        name: fields.name,
        phone: fields.phone,
        email: fields.email,
        reason: 'tradeIn',
        vehicle: `${car.brand} ${car.model} ${car.version} ${car.year}`,
        message: `Entrega en permuta: ${fields.brand} ${fields.model} ${fields.year}, ${fields.km} km, estado ${fields.condition.toLowerCase()}.`,
        tradeInVehicle: {
          marca: fields.brand.trim(),
          modelo: fields.model.trim(),
          anio: fields.year,
          kilometraje: fields.km.trim(),
          estadoGeneral: fields.condition
        }
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  return <form onSubmit={handleSubmit} noValidate className="space-y-5" data-id="element-369" data-mp="1ljcpjkxtgh9">
      <div data-id="element-370" data-mp="1jq0zpvgw62e">
        <p className="mb-2 text-xs text-muted" data-id="element-371" data-mp="3ssdlk3lvvp8w">Querés este vehículo</p>
        <CarSummary car={car} data-id="element-372" data-mp="3sq9an1pkhpz4" />
      </div>

      <p className="text-sm font-medium text-white" data-id="element-373" data-mp="ub9pnpau2cbs">Tu vehículo actual</p>
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-374" data-mp="pbnedlekf65m">
        <FormField label="Marca" htmlFor="ti-brand" error={errors.brand} data-id="element-375" data-mp="pcyijmbxafx9">
          <input id="ti-brand" className={inputClass} value={fields.brand} onChange={update('brand')} placeholder="Ej: Chevrolet" data-id="element-376" data-mp="kc0y133fx9sl" />
        </FormField>
        <FormField label="Modelo" htmlFor="ti-model" error={errors.model} data-id="element-377" data-mp="ffk1bki6dn3w">
          <input id="ti-model" className={inputClass} value={fields.model} onChange={update('model')} placeholder="Ej: Cruze LTZ" data-id="element-378" data-mp="353kog3tizbcp" />
        </FormField>
        <FormField label="Año" htmlFor="ti-year" error={errors.year} data-id="element-379" data-mp="sshswo9eyygp">
          <div className="relative" data-id="element-380" data-mp="sqevvk9ug52h">
            <select id="ti-year" className={`${inputClass} appearance-none pr-9`} value={fields.year} onChange={update('year')} data-id="element-381" data-mp="xjq5ft61izy3">
              <option value="" data-id="element-382" data-mp="ivmxwfccng76">Seleccionar</option>
              {years.map(y => <option key={y} value={y} data-id="element-383" data-mp="nslo4msw7303">
                  {y}
                </option>)}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-384" data-mp="1bjirhb6mgrzq" />
          </div>
        </FormField>
        <FormField label="Kilómetros" htmlFor="ti-km" error={errors.km} data-id="element-385" data-mp="11jry5ctssj18">
          <input id="ti-km" inputMode="numeric" className={inputClass} value={fields.km} onChange={update('km')} placeholder="Ej: 65000" data-id="element-386" data-mp="16jmqzlx4viox" />
        </FormField>
        <FormField label="Estado general" htmlFor="ti-condition" error={errors.condition} className="sm:col-span-2" data-id="element-387" data-mp="3m9pr1l2d1g6p">
          <div className="relative" data-id="element-388" data-mp="3r7r8y2o1v9gl">
            <select id="ti-condition" className={`${inputClass} appearance-none pr-9`} value={fields.condition} onChange={update('condition')} data-id="element-389" data-mp="29bpga46iuyni">
              <option value="" data-id="element-390" data-mp="2ibvr1iau5kiz">Seleccionar</option>
              {conditions.map(c => <option key={c} value={c} data-id="element-391" data-mp="2ecmz8izcvwq2">
                  {c}
                </option>)}
            </select>
            <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-392" data-mp="1qm93ahdnhdq7" />
          </div>
        </FormField>
      </div>

      <p className="text-sm font-medium text-white" data-id="element-393" data-mp="1zfvv5weqlwu4">Tus datos</p>
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-394" data-mp="1un2qf8j9qoi1">
        <FormField label="Nombre y apellido" htmlFor="ti-name" error={errors.name} className="sm:col-span-2" data-id="element-395" data-mp="1umb30kztrx2u">
          <input id="ti-name" className={inputClass} value={fields.name} onChange={update('name')} autoComplete="name" data-id="element-396" data-mp="16vnkl5sa9p21" />
        </FormField>
        <FormField label="Teléfono / WhatsApp" htmlFor="ti-phone" error={errors.phone} data-id="element-397" data-mp="321ydlqw7zbul">
          <input id="ti-phone" type="tel" className={inputClass} value={fields.phone} onChange={update('phone')} placeholder="11 5555 5555" autoComplete="tel" data-id="element-398" data-mp="36zrgob287pqn" />
        </FormField>
        <FormField label="Email" htmlFor="ti-email" error={errors.email} optional data-id="element-399" data-mp="3gq2wovz6ppdw">
          <input id="ti-email" type="email" className={inputClass} value={fields.email} onChange={update('email')} autoComplete="email" data-id="element-400" data-mp="3gs5onljwdsl9" />
        </FormField>
      </div>

      {status === 'error' && <SubmitError data-id="element-401" data-mp="2o3huf0h67svk" />}

      <button type="submit" disabled={status === 'loading'} className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-neon text-sm font-semibold text-ink transition-colors duration-150 hover:bg-neon-bright disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon focus-visible:ring-offset-2 focus-visible:ring-offset-panel" data-id="element-402" data-mp="2t0i37j5en9qd">
        {status === 'loading' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-403" data-mp="1xro6yvxmjtmr" />}
        {status === 'loading' ? 'Enviando…' : status === 'error' ? 'Reintentar' : 'Cotizar mi permuta'}
      </button>
    </form>;
}